import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { appLoginUrl, appSignupUrl } from '../../config';
import { isLive } from '../../config/liveRoutes';
import { useMountEffect } from '../../hooks/useMountEffect';
import BrandLogoNg from './BrandLogoNg';
import NgMobileMenu from './NgMobileMenu';
import '../../styles/ng.css';

/* The chrome of every comtev.ng page: minimal sticky nav, full-sheet mobile
   menu, single roomy footer. This is the NG counterpart of PlannerShell, built
   on the ngl system and the balloon brand — no lp3 classes, no BookDemoModal,
   no corporate mega-nav. `ctaTag` lands on the primary CTA as data-cta for
   analytics, same convention as the rest of the site. */

interface NgShellProps {
    children: React.ReactNode;
    ctaTag: string;
    className?: string;
    footerTagline?: string;
}

const NAV_LINKS: { to: string; label: string; live: boolean }[] = [
    { to: '/how-it-works', label: 'how it works', live: isLive('howItWorks') },
    { to: '/pricing', label: 'pricing', live: isLive('pricing') },
    { to: '/blog', label: 'guides', live: isLive('blogIndex') },
    { to: '/about', label: 'about', live: isLive('about') },
    { to: '/contact', label: 'contact', live: isLive('contact') },
];

const NgShell: React.FC<NgShellProps> = ({ children, ctaTag, className, footerTagline }) => {
    const [menuOpen, setMenuOpen] = useState(false);

    /* DEV-ONLY in effect: `npm start` serves public/index.html raw, so BOTH
       markets' data-market-gated icon links are in the head and the browser
       may keep showing the global "Sprout" favicon. Dropping the global-gated
       links leaves the balloon set the only candidate. In production builds
       apply-market.js has already stripped the attribute and the other
       market's tags, so this selector matches nothing and the effect is a
       no-op. */
    useMountEffect(() => {
        document.head
            .querySelectorAll('link[data-market="global"]')
            .forEach((el) => el.remove());
    });

    return (
        <div className={`ngl${className ? ` ${className}` : ''}`}>
            <header className="ngl-nav">
                <div className="ngl-container ngl-nav-in">
                    <Link to="/" className="ngl-nav-logo" aria-label="comtev home">
                        <BrandLogoNg height={26} />
                    </Link>

                    <nav className="ngl-nav-links" aria-label="Main">
                        {NAV_LINKS.filter((l) => l.live).map((l) => (
                            <Link key={l.to} to={l.to} className="ngl-nav-link">
                                {l.label}
                            </Link>
                        ))}
                    </nav>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 22 }}>
                        <a href={appLoginUrl()} className="ngl-nav-login">
                            log in
                        </a>
                        <a
                            href={appSignupUrl()}
                            className="ngl-nav-cta"
                            data-cta={ctaTag}
                        >
                            start free
                        </a>
                        <button
                            type="button"
                            className="ngl-nav-burger"
                            aria-label="Open menu"
                            aria-expanded={menuOpen}
                            onClick={() => setMenuOpen(true)}
                        >
                            <svg
                                width="22"
                                height="22"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.6"
                            >
                                <path d="M3 7h18M3 12h18M3 17h18" />
                            </svg>
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {menuOpen && (
                    <NgMobileMenu
                        links={NAV_LINKS.filter((link) => link.live)}
                        ctaTag={ctaTag}
                        onClose={() => setMenuOpen(false)}
                    />
                )}
            </AnimatePresence>

            {children}

            <footer className="ngl-footer">
                <div className="ngl-container">
                    <div className="ngl-ft-grid">
                        <div className="ngl-ft-brand">
                            <BrandLogoNg height={24} />
                            <p className="ngl-ft-tag">
                                {footerTagline || 'free event software for event planners. you run the event, we carry the list.'}
                            </p>
                        </div>
                        <div className="ngl-ft-col">
                            <h4>product</h4>
                            {isLive('howItWorks') && (
                                <Link to="/how-it-works">how it works</Link>
                            )}
                            {isLive('pricing') && <Link to="/pricing">pricing</Link>}
                            {isLive('blogIndex') && <Link to="/blog">guides</Link>}
                            {isLive('invoiceGenerator') && (
                                <Link to="/invoice-generator">invoice generator</Link>
                            )}
                            <a href={appSignupUrl()}>start free</a>
                        </div>
                        <div className="ngl-ft-col">
                            <h4>company</h4>
                            {isLive('about') && <Link to="/about">about</Link>}
                            {isLive('contact') && <Link to="/contact">contact</Link>}
                            {isLive('partners') && <Link to="/partners">partners</Link>}
                        </div>
                    </div>
                    <div className="ngl-ft-bot">
                        <span>
                            © {new Date().getFullYear()} comtev · ikeja, lagos
                        </span>
                        <span>
                            {isLive('privacy') && <Link to="/privacy">privacy</Link>}
                            {isLive('terms') && <Link to="/terms">terms</Link>}
                        </span>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default NgShell;
